import React, {useCallback, useEffect, useRef, useState} from "react"
import PropTypes from "prop-types"
import {Box, Typography} from "@mui/material"

const DEFAULT_MIN_ROWS = 1
const DEFAULT_MAX_ROWS = 8
const ROW_HEIGHT = 24
const PADDING_VERTICAL = 20 // keep in sync with textarea padding
const PADDING_HORIZONTAL = 12
const SCROLL_ROWS = 5
const BASE_HEIGHT_PX = 48

const DynamicTextarea = ({
    label,
    value,
    onChange,
    minRows = DEFAULT_MIN_ROWS,
    maxRows = DEFAULT_MAX_ROWS,
    disabled,
    error,
    helperText,
    inputProps,
    ...rest
}) => {
    const textareaRef = useRef(null)
    const paddingTotal = PADDING_VERTICAL + PADDING_VERTICAL / 2
    const maxHeight = maxRows * ROW_HEIGHT + paddingTotal
    const scrollHeight = Math.min(maxHeight, SCROLL_ROWS * ROW_HEIGHT + paddingTotal)

    const [height, setHeight] = useState(BASE_HEIGHT_PX)
    const [focused, setFocused] = useState(false)
    const [showScroll, setShowScroll] = useState(false)
    
    const hasPlaceholder = Boolean(inputProps?.placeholder) || Boolean(rest?.placeholder)

    const resize = useCallback(
        (target) => {
            const node = target || textareaRef.current
            if (!node) return
            // node.style.height = "auto"
            const rawHeight = node.scrollHeight - 2
            const hasContent = Boolean((node.value || "").length)
            const minHeight = hasContent ? Math.max(BASE_HEIGHT_PX, rawHeight) : BASE_HEIGHT_PX
            const cappedHeight = Math.min(maxHeight, scrollHeight)
            const nextHeight = Math.min(cappedHeight, minHeight)
            setShowScroll(rawHeight > cappedHeight + 2)
            // node.style.height = `${nextHeight}px`
            setHeight(nextHeight)
        },
        [maxHeight, scrollHeight]
    )

    useEffect(() => {
        resize()
    }, [value, resize])

    const handleChange = (event) => {
        onChange?.(event)
        resize(event.target)
    }

    const shrink = focused || Boolean(value) || hasPlaceholder
    const isReadonly = rest.readOnly
    const isDisabled = disabled || isReadonly
    const baseBg = isDisabled ? "#24293c" : "#32384c"
    const baseBorder = error ? "#f87171" : "#a0a6b5"
    const baseColor = isDisabled ? "#9aa0ae" : "#ffffff"
    const hoverBg = isDisabled ? "#54596a" : "#54596a"
    const hoverBorder = error ? "#f87171" : "#c5cad9"

    return (
        <Box
            sx={{
                width: "100%",
                position: "relative",
                "& .dynamic-textarea": {
                    width: "100%",
                    resize: "none",
                    padding: `${PADDING_VERTICAL}px ${PADDING_HORIZONTAL}px ${PADDING_VERTICAL / 2}px ${PADDING_HORIZONTAL}px`,
                    borderRadius: 0,
                    border: `1px solid ${baseBorder}`,
                    backgroundColor: baseBg,
                    color: baseColor,
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontFamily: "Titillium Web, sans-serif",
                    boxSizing: "border-box",
                    outline: "none",
                    overflowY: showScroll ? "auto" : "hidden",
                    overflowX: "hidden",
                    opacity: isDisabled ? 0.7 : 1,
                    transition:
                        "height 0.2s ease, min-height 0.2s ease, max-height 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
                    display: "block",
                },
                "& .dynamic-textarea:not(:disabled):hover": {
                    backgroundColor: hoverBg,
                    borderColor: hoverBorder,
                },
                "& .dynamic-textarea:disabled:hover": {
                    backgroundColor: hoverBg,
                    color: baseColor,
                },
            }}
        >
            {label && (
                <Typography
                    variant="body2"
                sx={{
                    position: "absolute",
                    left: 12,
                    top: shrink ? 8 : "14px",
                    fontSize: shrink ? "10px" : "14px",
                    lineHeight: shrink ? "16px" : `${PADDING_VERTICAL}px`,
                    height: shrink ? "16px" : `${PADDING_VERTICAL}px`,
                    display: "flex",
                    alignItems: shrink ? "flex-start" : "center",
                    color: "common.white",
                        opacity: 0.8,
                        transition: "all 0.15s ease",
                        pointerEvents: "none",
                    }}
                >
                    {label}
                </Typography>
            )}
            <textarea
                ref={textareaRef}
                rows={minRows}
                value={value || ""}
                onChange={handleChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                disabled={isDisabled}
                style={{
                    minHeight: BASE_HEIGHT_PX,
                    maxHeight: maxHeight,
                    height,
                    transition: "height 0.2s ease, min-height 0.2s ease, max-height 0.2s ease",
                }}
                className="dynamic-textarea"
                {...inputProps}
                {...rest}
            />
            {error && helperText && (
                <Typography variant="caption" sx={{color: "#f87171", mt: 0.5, display: "block"}}>
                    {helperText}
                </Typography>
            )}
        </Box>
    )
}

DynamicTextarea.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    minRows: PropTypes.number,
    maxRows: PropTypes.number,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    inputProps: PropTypes.object,
}

export default DynamicTextarea
